import type { CSSProperties } from 'react'
import { z } from 'zod'

import { calendarColors, type CalendarColor } from '@/features/catalog/courses/course'
import { addDays, dayOf, utcNoon, type CalendarDay } from '@/shared/ui/calendar'

import { classStatuses } from '../class-form/class'

const sessionSchema = z.object({
  sessionId: z.string(),
  classId: z.string(),
  className: z.string(),
  organizationId: z.string(),
  status: z.enum(classStatuses),
  // A color Catalog adds later shows in the default blue rather than breaking the page.
  calendarColor: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  primaryInstructorId: z.string().nullish(),
  primaryInstructorName: z.string().nullish(),
  instructorIds: z.array(z.string()),
  classroomIds: z.array(z.string()),
})

/** A session on a calendar. */
export type CalendarSession = z.infer<typeof sessionSchema>

/** The days of the week, Sunday first, as the API names them. */
export const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const

/** A day of the week. */
export type WeekDay = (typeof weekDays)[number]

/** The day an organization's weeks start on when it has said nothing else, as its settings' default. */
export const defaultWeekStart: WeekDay = 'monday'

const calendarSchema = z.object({
  organizationId: z.string(),
  organizationName: z.string(),
  timeZone: z.string(),
  from: z.string(),
  to: z.string(),
  sessions: z.array(sessionSchema),
  classrooms: z.array(z.object({ classroomId: z.string(), name: z.string(), isExternal: z.boolean() })),
  instructors: z.array(z.object({ userProfileId: z.string(), firstName: z.string(), lastName: z.string(), displayName: z.string() })),
  weekStartsOn: z.enum(weekDays),
  businessHours: z.array(z.object({ day: z.enum(weekDays), opens: z.string(), closes: z.string() })),
  includesExternalClassrooms: z.boolean(),
})

/** One of an organization's calendars over a range of days, in its time zone. */
export type OrganizationCalendar = z.infer<typeof calendarSchema>

export function parseCalendar(body: unknown): OrganizationCalendar {
  return calendarSchema.parse(body)
}

/** The most days one calendar read covers: a month grid's six weeks, as the API allows. */
export const maxCalendarDays = 42

/**
 * Each course calendar color's fill and outline, as legacy's `calendar.css` draws them, so a class
 * keeps the color its course was given there.
 */
const colorStyles: Readonly<Record<CalendarColor, readonly [string, string]>> = {
  blue: ['#e2effd', '#0071ca'],
  darkBlue: ['#2858a5', '#2858a5'],
  gray: ['#d6d6d6', '#4f4f4f'],
  darkGray: ['#6f6f6f', '#6f6f6f'],
  green: ['#ecfef0', '#368f2b'],
  darkGreen: ['#368f2b', '#368f2b'],
  maroon: ['#e7bed1', '#813d5f'],
  darkMaroon: ['#93446b', '#813d5f'],
  olive: ['#edf9d6', '#64743e'],
  darkOlive: ['#64743e', '#64743e'],
  orange: ['#fef2e4', '#e2620d'],
  darkOrange: ['#e2620d', '#e2620d'],
  peach: ['#ffeaaa', '#865f01'],
  darkPeach: ['#c79930', '#c79930'],
  purple: ['#ddd0fa', '#6a5893'],
  darkPurple: ['#6a5893', '#6a5893'],
  red: ['#f8d8d8', '#ac0000'],
  darkRed: ['#c11a25', '#c11a25'],
  steel: ['#ebeff5', '#5c687f'],
  darkSteel: ['#6b7994', '#6b7994'],
  teal: ['#d2fbee', '#2c7e65'],
  darkTeal: ['#2c7e65', '#2c7e65'],
  yellow: ['#fefdd2', '#595936'],
  darkYellow: ['#b9b300', '#b9b300'],
}

function isCalendarColor(value: string): value is CalendarColor {
  return (calendarColors as readonly string[]).includes(value)
}

/**
 * The fill and outline of a class in its course's color, blue for one this page does not know.
 * The text is black or white, whichever reads better on the fill: several of legacy's own text
 * colors fall short of WCAG AA's 4.5:1, and the better of the two never does.
 */
export function colorStyle(color: string): CSSProperties {
  const [backgroundColor, borderColor] = colorStyles[isCalendarColor(color) ? color : 'blue']
  const fill = luminance(backgroundColor)
  return { backgroundColor, borderColor, color: 1.05 / (fill + 0.05) >= (fill + 0.05) / 0.05 ? '#ffffff' : '#000000' }
}

// WCAG's relative luminance of a `#rrggbb` color.
function luminance(hex: string): number {
  const [red = 0, green = 0, blue = 0] = [1, 3, 5].map((start) => {
    const channel = Number.parseInt(hex.slice(start, start + 2), 16) / 255
    return channel <= 0.039_28 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

/** Today in `timeZone`, such as the organization's, or in the viewer's browser without one. */
export function today(timeZone?: string): CalendarDay {
  return dayOf(new Date(), timeZone)
}

/** Whether `value` is a real day, `YYYY-MM-DD`. */
export function isDay(value: string | null): value is CalendarDay {
  return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) && utcNoon(value).toISOString().slice(0, 10) === value
}

/** How many days from `from` to `to`, counting both. */
export function dayCount(from: CalendarDay, to: CalendarDay): number {
  return Math.round((utcNoon(to).getTime() - utcNoon(from).getTime()) / 86_400_000) + 1
}

/** The day of the week, 0 for Sunday. */
export function weekday(day: CalendarDay): number {
  return utcNoon(day).getUTCDay()
}

/** The day `weekStart` that begins the week holding `day`. */
export function weekStartOf(day: CalendarDay, weekStart: WeekDay): CalendarDay {
  return addDays(day, -((weekday(day) - weekDays.indexOf(weekStart) + 7) % 7))
}

/** The six weeks, each starting on `weekStart`, that show `month` (`YYYY-MM`). */
export function monthGrid(month: string, weekStart: WeekDay): readonly CalendarDay[] {
  const start = weekStartOf(`${month}-01`, weekStart)
  return Array.from({ length: maxCalendarDays }, (_, index) => addDays(start, index))
}

/** The month `count` months from `month` (`YYYY-MM`). */
export function addMonths(month: string, count: number): string {
  const date = utcNoon(`${month}-01`)
  date.setUTCMonth(date.getUTCMonth() + count)
  return date.toISOString().slice(0, 7)
}

/** The days from `from` to `to`, weekends left out unless asked for. */
export function daysOf(from: CalendarDay, to: CalendarDay, includeWeekends: boolean): readonly CalendarDay[] {
  const days: CalendarDay[] = []
  // Stops on the last day itself: past 9999-12-31 the next day would not compare as later.
  for (let day = from; day <= to; day = addDays(day, 1)) {
    const index = weekday(day)
    if (includeWeekends || (index !== 0 && index !== 6)) {
      days.push(day)
    }
    if (day === to) {
      break
    }
  }
  return days
}

/** One numeric formatter per time zone, since building one is the slow part. */
const hourFormatters = new Map<string, Intl.DateTimeFormat>()

/** The hour (0 to 23) an instant falls in, in `timeZone`. */
export function hourOf(instant: Date, timeZone: string): number {
  let formatter = hourFormatters.get(timeZone)
  if (formatter === undefined) {
    // A fixed locale only to read the number back; nothing it formats is shown.
    formatter = new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', hourCycle: 'h23' })
    hourFormatters.set(timeZone, formatter)
  }
  return Number(formatter.formatToParts(instant).find((part) => part.type === 'hour')?.value ?? '0') % 24
}

/** The last instant a session takes up: just before it ends, so one ending on the hour does not take that hour. */
function lastInstant(session: Pick<CalendarSession, 'endsOn'>): Date {
  return new Date(new Date(session.endsOn).getTime() - 1)
}

/** The first and last days a session, or anything else with a start and an end, covers in `timeZone`; one ending at midnight ends the day before. */
export function daysCovered(session: Pick<CalendarSession, 'startsOn' | 'endsOn'>, timeZone: string): { readonly first: CalendarDay; readonly last: CalendarDay } {
  const first = dayOf(new Date(session.startsOn), timeZone)
  const last = dayOf(lastInstant(session), timeZone)
  return { first, last: last < first ? first : last }
}

/** The sessions on `day`, in their order. */
export function sessionsOn(sessions: readonly CalendarSession[], day: CalendarDay, timeZone: string): readonly CalendarSession[] {
  return sessions.filter((session) => {
    const { first, last } = daysCovered(session, timeZone)
    return first <= day && day <= last
  })
}

/** The hour row a session on `day` shows in: its start hour, or the first row for one that started the day before. */
export function rowOf(session: CalendarSession, day: CalendarDay, timeZone: string): number {
  return daysCovered(session, timeZone).first < day ? 0 : hourOf(new Date(session.startsOn), timeZone)
}

/** When an organization is open on one day of the week, as `HH:mm:ss`. */
export type BusinessDay = OrganizationCalendar['businessHours'][number]

/**
 * The hour rows a day shows: 8 AM to 5 PM, as legacy's, widened to the organization's business
 * hours that day and to every hour its sessions take up; from midnight for one that started the
 * day before, to the last hour for one that runs past it.
 */
export function hoursOf(
  sessions: readonly CalendarSession[],
  day: CalendarDay,
  timeZone: string,
  businessHours: readonly BusinessDay[] = [],
): readonly number[] {
  let first = 8
  let last = 17
  const open = businessHours.find((hours) => hours.day === weekDays[weekday(day)])
  if (open !== undefined) {
    const [opens = 0] = open.opens.split(':').map(Number)
    const [closes = 0, closesMinute = 0] = open.closes.split(':').map(Number)
    first = Math.min(first, opens)
    last = Math.max(last, closesMinute > 0 ? closes : closes - 1)
  }
  for (const session of sessions) {
    const covered = daysCovered(session, timeZone)
    first = Math.min(first, rowOf(session, day, timeZone))
    last = Math.max(last, covered.last > day ? 23 : hourOf(lastInstant(session), timeZone))
  }
  return Array.from({ length: last - first + 1 }, (_, index) => first + index)
}